import React, { useState } from 'react';
import { ClipboardCheck, Loader2, Target, Dumbbell, Utensils, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const FreeAssessmentPage = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    name: '', email: '', age: '', gender: '',
    current_weight: '', goal: '', target_weight: '',
    eating_habits: '', exercise_frequency: '', sessions_per_week: '2',
    biggest_challenge: ''
  });

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        age: form.age ? parseInt(form.age) : null,
        current_weight: parseFloat(form.current_weight),
        target_weight: form.target_weight ? parseFloat(form.target_weight) : null,
        sessions_per_week: parseInt(form.sessions_per_week)
      };
      const res = await axios.post(`${API}/api/fitness-assessment`, payload);
      setResult(res.data.projection);
    } catch (e) {
      setResult("Sorry, we couldn't generate your projection right now. Please try again or contact Aaron directly.");
    }
    setLoading(false);
  };

  const canProceed1 = form.name && form.email && form.current_weight && form.goal;
  const canProceed2 = form.eating_habits && form.exercise_frequency;

  return (
    <div className="assess-page" data-testid="assessment-page">
      <div className="assess-container">

        {/* Header */}
        <div className="assess-header">
          <ClipboardCheck size={36} className="assess-header-icon" />
          <h1>Free Fitness Assessment</h1>
          <p>Find out what you could realistically achieve with the right guidance. Takes 2 minutes.</p>
        </div>

        {!result && !loading && (
          <>
            {/* Progress Bar */}
            <div className="assess-progress">
              <div className="assess-progress-bar" style={{ width: step === 1 ? '33%' : step === 2 ? '66%' : '100%' }} />
            </div>

            {/* Step 1: About You */}
            {step === 1 && (
              <div className="assess-step" data-testid="assess-step-1">
                <div className="assess-step-header">
                  <Target size={22} />
                  <div>
                    <h2>About You & Your Goals</h2>
                    <p>Let's start with the basics so we can give you an accurate projection.</p>
                  </div>
                </div>

                <div className="assess-form-grid">
                  <div className="assess-field">
                    <label>Your First Name *</label>
                    <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. James" data-testid="assess-name" />
                  </div>
                  <div className="assess-field">
                    <label>Email *</label>
                    <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="you@email.com" data-testid="assess-email" />
                  </div>
                  <div className="assess-field">
                    <label>Age</label>
                    <input type="number" value={form.age} onChange={e => set('age', e.target.value)} placeholder="e.g. 32" />
                  </div>
                  <div className="assess-field">
                    <label>Gender</label>
                    <select value={form.gender} onChange={e => set('gender', e.target.value)}>
                      <option value="">Prefer not to say</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="assess-field">
                    <label>Current Weight (kg) *</label>
                    <input type="number" step="0.1" value={form.current_weight} onChange={e => set('current_weight', e.target.value)} placeholder="e.g. 85" data-testid="assess-weight" />
                  </div>
                  <div className="assess-field">
                    <label>Target Weight (kg)</label>
                    <input type="number" step="0.1" value={form.target_weight} onChange={e => set('target_weight', e.target.value)} placeholder="e.g. 75" />
                  </div>
                </div>

                <div className="assess-field assess-field-full">
                  <label>What's your main goal? *</label>
                  <div className="assess-goal-grid" data-testid="assess-goal">
                    {[
                      { value: 'lose_weight', label: 'Lose Weight', desc: 'Drop body fat and slim down' },
                      { value: 'build_muscle', label: 'Build Muscle', desc: 'Get stronger and add size' },
                      { value: 'tone_up', label: 'Tone Up', desc: 'Leaner, more defined physique' },
                      { value: 'general_fitness', label: 'General Fitness', desc: 'Feel healthier and more energetic' }
                    ].map(g => (
                      <button key={g.value} className={`assess-goal-card ${form.goal === g.value ? 'active' : ''}`} onClick={() => set('goal', g.value)}>
                        <strong>{g.label}</strong>
                        <span>{g.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <Button onClick={() => setStep(2)} disabled={!canProceed1} className="assess-next-btn" data-testid="assess-next-1">
                  Next Step
                </Button>
              </div>
            )}

            {/* Step 2: Habits */}
            {step === 2 && (
              <div className="assess-step" data-testid="assess-step-2">
                <div className="assess-step-header">
                  <Utensils size={22} />
                  <div>
                    <h2>Your Current Habits</h2>
                    <p>Be honest — this helps us give you the most accurate projection. No judgement here.</p>
                  </div>
                </div>

                <div className="assess-field assess-field-full">
                  <label>Describe your general eating habits *</label>
                  <textarea value={form.eating_habits} onChange={e => set('eating_habits', e.target.value)} rows={3}
                    placeholder="e.g. I eat fairly well during the week but tend to overeat at weekends. I skip breakfast most days and probably don't eat enough protein..."
                    data-testid="assess-eating" />
                </div>

                <div className="assess-field assess-field-full">
                  <label>How often do you currently exercise? *</label>
                  <div className="assess-freq-grid" data-testid="assess-frequency">
                    {[
                      { value: 'none', label: 'Not at all', desc: "I haven't been active recently" },
                      { value: '1-2', label: '1-2 times/week', desc: 'Occasional gym or walks' },
                      { value: '3-4', label: '3-4 times/week', desc: 'Fairly consistent' },
                      { value: '5+', label: '5+ times/week', desc: 'Very active' }
                    ].map(f => (
                      <button key={f.value} className={`assess-freq-card ${form.exercise_frequency === f.value ? 'active' : ''}`} onClick={() => set('exercise_frequency', f.value)}>
                        <strong>{f.label}</strong>
                        <span>{f.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="assess-form-grid">
                  <div className="assess-field">
                    <label>How many PT sessions per week would suit you?</label>
                    <select value={form.sessions_per_week} onChange={e => set('sessions_per_week', e.target.value)}>
                      <option value="1">1 session</option>
                      <option value="2">2 sessions</option>
                      <option value="3">3 sessions</option>
                      <option value="4">4+ sessions</option>
                    </select>
                  </div>
                  <div className="assess-field">
                    <label>What's your biggest challenge?</label>
                    <input value={form.biggest_challenge} onChange={e => set('biggest_challenge', e.target.value)} placeholder="e.g. Staying consistent, motivation, knowing what to eat..." />
                  </div>
                </div>

                <div className="assess-btn-row">
                  <Button onClick={() => setStep(1)} className="assess-back-btn">Back</Button>
                  <Button onClick={submit} disabled={!canProceed2} className="assess-next-btn" data-testid="assess-submit">
                    Get My Free Projection
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Loading */}
        {loading && (
          <div className="assess-loading" data-testid="assess-loading">
            <Loader2 size={40} className="portal-spin" />
            <h3>Analysing your details...</h3>
            <p>We're creating your personalised fitness projection. This takes about 10 seconds.</p>
          </div>
        )}

        {/* Result */}
        {result && !loading && (
          <div className="assess-result" data-testid="assess-result">
            <div className="assess-result-card">
              <h2>Your Personalised Projection</h2>
              <div className="assess-result-body">
                {result.split('\n').map((line, i) => {
                  if (!line.trim()) return null;
                  if (line.startsWith('**') && line.endsWith('**')) {
                    return <h3 key={i} className="assess-result-heading">{line.replace(/\*\*/g, '')}</h3>;
                  }
                  if (line.includes('**')) {
                    const parts = line.split('**');
                    return <p key={i}>{parts.map((part, j) => j % 2 === 1 ? <strong key={j}>{part}</strong> : part)}</p>;
                  }
                  return <p key={i}>{line}</p>;
                })}
              </div>
            </div>

            <div className="assess-cta-section">
              <h3>Ready to get started?</h3>
              <p>Try 2 introductory sessions for just <strong>£50</strong> — no commitment, just a chance to see what's possible.</p>
              <div className="assess-cta-buttons">
                <a href="https://wa.me/447787166065?text=Hi%20Aaron%2C%20I%20just%20did%20the%20fitness%20assessment%20on%20your%20website%20and%20I%27d%20love%20to%20chat%20about%20getting%20started" target="_blank" rel="noopener noreferrer" className="assess-whatsapp-btn" data-testid="assess-whatsapp-cta">
                  <MessageCircle size={20} /> Chat with Aaron on WhatsApp
                </a>
                <Button onClick={() => { setResult(null); setStep(1); setForm({ name: '', email: '', age: '', gender: '', current_weight: '', goal: '', target_weight: '', eating_habits: '', exercise_frequency: '', sessions_per_week: '2', biggest_challenge: '' }); }} className="assess-back-btn">
                  Start Again
                </Button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default FreeAssessmentPage;
